import { Grid } from '@material-ui/core';
import {
  DefaultCard,
  DisplayItem,
  FormHookResult,
  NetworkIndicator,
  SearchableDropDown,
} from 'design-system';
import React, { useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

import { PersonType } from 'src/functions/validationNames';
import { RootState } from '../../../../store/rootReducer';

import {
  getCountries,
  getOccupations,
} from '../../../../store/legalEntityCatalog/actions';
import {
  COUNTRIES_REQUEST,
  OCCUPATIONS_REQUEST,
} from '../../../../store/legalEntityCatalog/actions/legalEntityRequestIdentifiers';
import { makeTextField } from '../../../MakeFormFields';

type MakeDropDownProps = {
  inputName: string;
  label: string;
  getInputProps: FormHookResult;
  options: DisplayItem[];
  requesting: boolean;
  inputId: string;
  isDisabled: boolean;
};
function makeDropDown({
  inputName,
  label,
  getInputProps,
  options,
  requesting,
  inputId,
  isDisabled,
}: MakeDropDownProps): React.ReactNode {
  const { value, onChange, error, errorText } = getInputProps(inputName);
  if (requesting) {
    return (
      <NetworkIndicator
        data-testid={`${inputName}-loading`}
        requesting
        message="Cargando..."
        variant="horizontal"
      />
    );
  }
  return (
    <SearchableDropDown
      inputId={inputId}
      inputName={inputName}
      inputLabel={label}
      defaultValue={value as string}
      onChange={(newVal: DisplayItem): void => {
        onChange(newVal.Value);
      }}
      inputError={error}
      inputErrorMessage={errorText}
      options={options}
      isDisabled={isDisabled}
    />
  );
}

export const GeneralLegalFieldsCard = ({
  getInputProps,
  countries,
  occupations,
  getCountriesAction,
  getOccupationsAction,
  isDisabled = false,
}: Props): JSX.Element => {
  useEffect(() => {
    getCountriesAction();
    getOccupationsAction();
  }, [getCountriesAction, getOccupationsAction]);

  const countriesRequestStatus = useNetworkObserver(COUNTRIES_REQUEST);
  const occupationsRequestStatus = useNetworkObserver(OCCUPATIONS_REQUEST);

  const countryDisplayItems = useMemo(
    () =>
      countries.map(
        (country): DisplayItem => ({ Text: country.name, Value: country.code }),
      ),
    [countries],
  );
  const occupationsDisplayItems = useMemo(
    () =>
      occupations.map(
        (occupation): DisplayItem => ({
          Text: occupation.name,
          Value: occupation.code,
        }),
      ),
    [occupations],
  );

  return (
    <DefaultCard title="Datos del Contratante">
      <Grid container spacing={3} style={{ marginTop: 8 }}>
        <Grid item xs={12} md={6}>
          {makeTextField({
            inputName: 'legalEntityName',
            label: 'Nombre de la empresa',
            getInputProps,
            inputId: 'sales-web-inspection-input-legalEntityName',
            isDisabled,
            personType: PersonType.legal,
          })}
        </Grid>
        <Grid item xs={12} md={6}>
          {makeTextField({
            inputName: 'phoneNumber',
            label: 'Celular o teléfono',
            getInputProps,
            inputId: 'sales-web-inspection-input-phoneNumber',
            isDisabled,
          })}
        </Grid>
        <Grid item xs={12} md={6}>
          {makeTextField({
            inputName: 'email',
            label: 'Correo Electrónico',
            getInputProps,
            inputId: 'sales-web-inspection-input-email',
            helperText:
              'Este correo se utilizará para realizar la firma digital',
            isDisabled,
          })}
        </Grid>
        <Grid item xs={12} md={6}>
          {makeDropDown({
            inputName: 'legalEntityCountry',
            label: 'País',
            getInputProps,
            options: countryDisplayItems,
            requesting: countriesRequestStatus === 'requesting',
            inputId: 'sales-web-inspection-input-legalEntityCountry',
            isDisabled,
          })}
        </Grid>
        <Grid item xs={12} md={6}>
          {makeDropDown({
            inputName: 'legalEntityOccupation',
            label: 'Actividad económica de la empresa',
            getInputProps,
            options: occupationsDisplayItems,
            requesting: occupationsRequestStatus === 'requesting',
            inputId: 'sales-web-inspection-input-legalEntityOccupation',
            isDisabled,
          })}
        </Grid>
      </Grid>
    </DefaultCard>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  countries: GeneralCatalogItem[];
  occupations: GeneralCatalogItem[];
} => ({
  countries: state.legalEntityCatalog.countries,
  occupations: state.legalEntityCatalog.occupations,
});

const reduxActions = {
  getCountriesAction: getCountries,
  getOccupationsAction: getOccupations,
};
const connector = connect(mapStateToProps, reduxActions);

type PropsFromRedux = ConnectedProps<typeof connector>;
interface Props extends PropsFromRedux {
  getInputProps: FormHookResult;
  isDisabled?: boolean;
}

export default connector(GeneralLegalFieldsCard);
